import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { normalizeDashboardAnalytics } from '../model/dashboard.model';
import { appendCriteriaEquals, appendCriteriaIn, readPagedRows } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const analyticsUrl = API_ROUTES.kasesDashboard;
const initialState = {
    loading: false,
    referenceLoading: false,
    errorMessage: null,
    reference: null,
    analytics: null,
};
function buildQueryParams(params) {
    const q = new URLSearchParams();
    appendCriteriaEquals(q, 'startDate', params.startDate);
    appendCriteriaEquals(q, 'endDate', params.endDate);
    appendCriteriaIn(q, 'issueId', params.issueIds);
    appendCriteriaIn(q, 'provinceId', params.provinceIds);
    appendCriteriaIn(q, 'districtId', params.districtIds);
    return q;
}
export const getDashboardReference = createAsyncThunk('dashboard/reference', async () => {
    const [issuesRes, provincesRes, districtsRes] = await Promise.all([
        axios.get(`${API_ROUTES.issues}?page=0&size=1000&sort=id,asc`),
        axios.get(`${API_ROUTES.provinces}?page=0&size=40&sort=id,asc`),
        axios.get(`${API_ROUTES.districts}?page=0&size=1000&sort=id,asc`),
    ]);
    const { rows: issueRows } = readPagedRows(issuesRes.data, issuesRes.headers);
    const { rows: provinceRows } = readPagedRows(provincesRes.data, provincesRes.headers);
    const { rows: districtRows } = readPagedRows(districtsRes.data, districtsRes.headers);
    return {
        data: {
            issues: issueRows.map((i) => ({ id: String(i.id ?? ''), name: i.name ?? '' })),
            provinces: provinceRows.map((p) => ({ id: String(p.id ?? ''), name: p.name ?? '' })),
            districts: districtRows.map((d) => ({
                id: String(d.id ?? ''),
                name: d.name ?? '',
                provinceId: d.provinceId != null
                    ? String(d.provinceId)
                    : d.province?.id != null
                        ? String(d.province.id)
                        : undefined,
            })),
        },
    };
}, { serializeError: serializeAxiosError });
export const getDashboardAnalytics = createAsyncThunk('dashboard/analytics', async (params) => {
    const q = buildQueryParams(params);
    const qs = q.toString();
    return axios.get(`${analyticsUrl}${qs ? `?${qs}` : ''}`);
}, { serializeError: serializeAxiosError });
export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(getDashboardReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload.data;
        })
            .addCase(getDashboardAnalytics.fulfilled, (state, action) => {
            state.loading = false;
            state.analytics = normalizeDashboardAnalytics(action.payload.data);
        })
            .addMatcher(isPending(getDashboardReference), (state) => {
            state.referenceLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getDashboardAnalytics), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getDashboardReference, getDashboardAnalytics), (state, action) => {
            state.loading = false;
            state.referenceLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export default dashboardSlice.reducer;
